/* eslint-disable react/display-name */
import { useMergeRefs } from 'rooks';
import { ReactNode, forwardRef, useRef } from 'react';
import { css as emotionCSS } from '@emotion/react';
import { token } from '@mildang/styled-system/tokens';
import { TextField as MuiTextField, TextFieldProps as MuiTextFieldProps } from '@mildang/mui';
import { InputAdornment as InputAdornment } from '@mildang/mui';
import { FormLabel as FormLabel, formLabelClasses } from '@mildang/mui';
import ErrorOutlineIcon from '@mildang/icons/react/error-outline';
import CheckIcon from '@mildang/icons/react/check';
import type { Theme } from '@mildang/mui';
import { inputLabelClasses, outlinedInputClasses } from '@mildang/mui';

const noop = () => {};

type Size = 'small' | 'medium' | 'large' | 'xLarge';

export interface TextFieldPropsSizeOverrides {
  theme: Theme;
  success?: boolean;
  size?: Size;
  hideBorderOnIdle?: boolean;
  disabled?: boolean;
}

const rootStyle = ({
  theme,
  success = false,
  size,
  hideBorderOnIdle,
  disabled = false,
}: TextFieldPropsSizeOverrides) => emotionCSS`
  &.MuiButton-sizeLarge {
    min-width: 42px;
    padding: 6px 9px;
  }
  &.MuiButton-sizeMedium {
    min-width: 36px;
    padding: 6px 6px;
  }
  &.MuiButton-sizeSmall {
    min-width: 32px;
    padding: 4px 4px;
  }
  & .${outlinedInputClasses.input} {
    ::placeholder {
      opacity: 1;
      color: ${token('colors.common.10')};
    }
  }

  ${
    hideBorderOnIdle &&
    emotionCSS`
    & .${outlinedInputClasses.root} {
      background-color: transparent;
    }

    & .${outlinedInputClasses.root}:hover .${outlinedInputClasses.notchedOutline} {
      border-color: #d5d5d7;
      border-width: 1px;
    }
    && .${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline} {
      border-color: ${theme.palette.primary.main};
      border-width: 1px;
    }
    & .${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline} {
      border-color: transparent;
    }

    & .${outlinedInputClasses.input} {
      padding-left: 0;
    }

    & .${outlinedInputClasses.root}:hover .${outlinedInputClasses.input} {
      padding-left: 12px;
    }

    & .${outlinedInputClasses.focused} .${outlinedInputClasses.input} {
      padding-left: 12px;
    }

    & .MuiInputLabel-root.${inputLabelClasses.shrink} {
      transform: translate(14px, -9px) scale(0.75);
    }

    & .${outlinedInputClasses.root}.${outlinedInputClasses.disabled} .${outlinedInputClasses.notchedOutline} {
      pointer-events: none;
      border-color: transparent;
    }
  `
  }

  ${
    size === 'large' &&
    emotionCSS`
    & .MuiInputLabel-root {
      font-size: ${theme.typography.pxToRem(15)};
      transform: translate(14px, 8px) scale(1);
    }
    & .MuiInputAdornment-root svg {
      font-size: ${theme.typography.pxToRem(22)};
    }
  `
  }
  ${
    size === 'medium' &&
    emotionCSS`
    & .MuiInputLabel-root {
      font-size: ${theme.typography.pxToRem(14)};
      transform: translate(14px, 6px) scale(1);
    }
    & .MuiInputAdornment-root svg {
      font-size: ${theme.typography.pxToRem(20)};
    }
  `
  }
  ${
    size === 'small' &&
    emotionCSS`
    & .MuiInputLabel-root {
      font-size: ${theme.typography.pxToRem(13)};
      transform: translate(14px, 5px) scale(1);
    }
    & .MuiInputAdornment-root svg {
      font-size: ${theme.typography.pxToRem(18)};
    }
  `
  }
  & .${inputLabelClasses.root}.${inputLabelClasses.shrink} {
    transform: translate(14px, -9px) scale(0.75);
  }

  ${
    success === true &&
    emotionCSS`
    && {
      & .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.palette.secondary.stroke};
      }
      &:hover .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.palette.secondary.strokeHover};
      }
      & .Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.palette.secondary.main};
      }

      & .MuiInputLabel-shrink {
        color: ${theme.palette.secondary.darkActive};
      }
      & .Mui-focused.MuiInputLabel-shrink {
        color: ${theme.palette.secondary.dark};
      }

      & .MuiInputAdornment-outlined svg {
        color: ${theme.palette.secondary.dark};
      }
      & .MuiFormHelperText-root {
        color: ${theme.palette.secondary.dark};
      }
    }
  `
  }

  ${
    disabled === true &&
    emotionCSS`
    && {
      .${outlinedInputClasses.root} {
        opacity: ${theme.opacity.disabled};
        & .${outlinedInputClasses.input}.${outlinedInputClasses.disabled} {
          color: ${theme.palette.text.tertiary};
          -webkit-text-fill-color: ${theme.palette.text.tertiary};
        }
      }
    }
  `
  }

  /* disable browser default autocomplete style */
  & input:-webkit-autofill,
          input:-webkit-autofill:hover,
          input:-webkit-autofill:focus,
          input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px white inset !important;
    box-shadow: 0 0 0 30px white inset !important;
  }
`;

const formLabelStyle = (theme: Theme) => emotionCSS`
  display: block;
  &&.${formLabelClasses.disabled} {
    color: ${theme.palette.text.tertiary};
  }
`;

export type CustomTextFieldProps = Omit<MuiTextFieldProps, 'variant'> & {
  success?: boolean;
  enableAutoEndIcon?: boolean;
  hideBorderOnIdle?: boolean;
  variant?: MuiTextFieldProps['variant'] | 'separated';
  fullHeight?: boolean;
  textFieldWidth?: number;
};

const TextField = forwardRef<any, CustomTextFieldProps>(
  (
    {
      hideBorderOnIdle = false,
      enableAutoEndIcon = false,
      success = false,
      size = 'xLarge',
      label,
      variant = 'outlined',
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) => {
    let endAdornment: ReactNode | undefined;
    const inputRef = useRef<HTMLInputElement | null>(null);
    // https://github.com/imbhargav5/rooks/blob/main/src/hooks/useMergeRefs.ts
    const mergedInputRef = useMergeRefs(inputRef, props?.inputRef || noop);

    if (enableAutoEndIcon && !props?.InputProps?.endAdornment) {
      if (props?.error) {
        endAdornment = (
          <InputAdornment data-testid="TextField_error_endAdornment" position="end">
            <ErrorOutlineIcon />
          </InputAdornment>
        );
      } else if (success) {
        endAdornment = (
          <InputAdornment data-testid="TextField_success_endAdornment" position="end">
            <CheckIcon />
          </InputAdornment>
        );
      }
    }

    const textfield = (
      <MuiTextField
        ref={ref}
        css={(theme: any) =>
          rootStyle({
            theme,
            success,
            size,
            hideBorderOnIdle,
            disabled: props.disabled,
          })
        }
        {...props}
        variant={variant === 'separated' ? 'outlined' : variant}
        label={variant !== 'separated' ? label : undefined}
        size={size}
        inputRef={mergedInputRef}
        onBlur={onBlur}
        InputProps={{
          ...props.InputProps,
          ...(endAdornment ? { endAdornment } : {}),
        }}
      />
    );

    if (variant !== 'separated') {
      return textfield;
    }
    return (
      <div
        css={emotionCSS`
          ${props.fullWidth ? 'width: 100%' : ''};
          ${props.fullHeight ? 'height: 100%' : ''}
        `}
      >
        {variant === 'separated' && label && (
          <FormLabel
            css={formLabelStyle}
            htmlFor={props.id}
            required={props.required}
            disabled={props.disabled}
          >
            {label}
          </FormLabel>
        )}
        {textfield}
      </div>
    );
  },
);

export default TextField;
